import { useEffect, useMemo, useState } from 'react'
import { Loader2, Search, UsersRound, GraduationCap, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useAdminUsers } from '@/services/admin/users.service'
import { useCompanies } from '@/services/admin/companies.service'
import {
  useAdminCourses,
  useBulkEnroll,
  useCourseEnrollments,
} from '@/services/admin/courses.service'
import { getErrorMessage } from '@/utils/error'

interface BulkEnrollDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Cadastro em Massa — o super_admin escolhe empresa + curso, vê todos os
 * usuários da empresa pré-selecionados e matricula todo mundo de uma vez.
 *
 * Usuários já matriculados aparecem na lista apenas como informação (selo +
 * checkbox desabilitado) — não entram na operação. Durante a escrita o dialog
 * trava por completo com um overlay de espera.
 */
export function BulkEnrollDialog({ open, onOpenChange }: BulkEnrollDialogProps) {
  const { toast } = useToast()
  const [companyId, setCompanyId] = useState('')
  const [courseId, setCourseId] = useState('')
  const [search, setSearch] = useState('')
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set())

  const numericCourseId = courseId ? Number(courseId) : null

  const { data: companies } = useCompanies(open)
  const { data: courses } = useAdminCourses()
  const { data: users, isLoading: usersLoading } = useAdminUsers(undefined, companyId || undefined)
  const { data: enrollments, isLoading: enrollmentsLoading } = useCourseEnrollments(numericCourseId)

  const bulkEnroll = useBulkEnroll()
  const isPending = bulkEnroll.isPending

  // Cursos elegíveis: publicados e da empresa escolhida (ou globais).
  const courseOptions = useMemo(() => {
    if (!companyId) return []
    return (courses ?? []).filter(
      (c) => c.status === 'published' && (c.companyId == null || c.companyId === companyId)
    )
  }, [courses, companyId])

  // Quem já está no curso (para não re-oferecer).
  const enrolledSet = useMemo(
    () => new Set((enrollments ?? []).map((e) => e.userId)),
    [enrollments]
  )

  // Usuários da empresa que ainda NÃO estão no curso — os selecionáveis.
  const selectableUsers = useMemo(
    () => (users ?? []).filter((u) => !enrolledSet.has(u.id)),
    [users, enrolledSet]
  )

  const enrolledInCompany = useMemo(
    () => (users ?? []).filter((u) => enrolledSet.has(u.id)),
    [users, enrolledSet]
  )

  // Pré-seleciona todos os selecionáveis sempre que a lista (re)carrega.
  // Só dispara quando users/enrollments mudam — toggles manuais não mexem aqui.
  useEffect(() => {
    setSelectedUserIds(new Set(selectableUsers.map((u) => u.id)))
  }, [selectableUsers])

  const bothChosen = Boolean(companyId && courseId)
  const listLoading = usersLoading || enrollmentsLoading

  // A busca filtra apenas o que é renderizado — não mexe na seleção.
  const visibleUsers = useMemo(() => {
    const list = users ?? []
    const q = search.trim().toLowerCase()
    if (!q) return list
    return list.filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
  }, [users, search])

  const allSelectableSelected =
    selectableUsers.length > 0 && selectableUsers.every((u) => selectedUserIds.has(u.id))

  const handleCompanyChange = (value: string) => {
    setCompanyId(value)
    setCourseId('')
    setSelectedUserIds(new Set())
    setSearch('')
  }

  const handleCourseChange = (value: string) => {
    setCourseId(value)
    setSearch('')
  }

  const toggleAll = (checked: boolean) => {
    setSelectedUserIds(checked ? new Set(selectableUsers.map((u) => u.id)) : new Set())
  }

  const toggleOne = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  // Trava: não fecha enquanto a operação está em andamento.
  const handleOpenChange = (next: boolean) => {
    if (isPending) return
    onOpenChange(next)
  }

  const handleSubmit = async () => {
    if (!numericCourseId || selectedUserIds.size === 0) return

    try {
      const result = await bulkEnroll.mutateAsync({
        courseId: numericCourseId,
        userIds: [...selectedUserIds],
      })

      const parts = [`${result.enrolled} usuário(s) matriculado(s)`]
      if (result.alreadyEnrolled > 0) parts.push(`${result.alreadyEnrolled} já estavam no curso`)
      if (result.skipped > 0) parts.push(`${result.skipped} ignorado(s)`)

      toast({ title: 'Cadastro em massa concluído', description: parts.join(' · ') })
      onOpenChange(false)
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: 'Erro no cadastro em massa',
        description: getErrorMessage(error, 'Não foi possível concluir o cadastro.'),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[620px] overflow-hidden"
        onEscapeKeyDown={(e) => isPending && e.preventDefault()}
        onInteractOutside={(e) => isPending && e.preventDefault()}
        onPointerDownOutside={(e) => isPending && e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <UsersRound className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle>Cadastro em Massa</DialogTitle>
              <DialogDescription>
                Matricule toda a empresa em um curso de uma só vez.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Empresa + Curso */}
          <div className="grid gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Empresa</label>
              <Select value={companyId} onValueChange={handleCompanyChange} disabled={isPending}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companies
                    ?.filter((c) => c.active)
                    .map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Curso</label>
              <Select
                value={courseId}
                onValueChange={handleCourseChange}
                disabled={isPending || !companyId}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={companyId ? 'Selecione o curso' : 'Escolha a empresa primeiro'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {courseOptions.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      Nenhum curso publicado disponível
                    </div>
                  ) : (
                    courseOptions.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.title}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Lista de usuários */}
          <div className="rounded-lg border border-border/60">
            {/* Cabeçalho da lista: busca + selecionar todos */}
            <div className="flex items-center gap-3 border-b border-border/60 p-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-9 pl-9"
                  disabled={isPending || !bothChosen}
                />
              </div>
              {bothChosen && selectableUsers.length > 0 && (
                <label className="flex shrink-0 items-center gap-2 text-sm text-muted-foreground">
                  <Checkbox
                    checked={allSelectableSelected}
                    onCheckedChange={(c) => toggleAll(c === true)}
                    disabled={isPending}
                  />
                  Todos
                </label>
              )}
            </div>

            <ScrollArea className="h-[260px]">
              {!bothChosen ? (
                <div className="flex h-[260px] flex-col items-center justify-center gap-2 px-6 text-center text-muted-foreground">
                  <GraduationCap className="h-8 w-8 opacity-40" />
                  <p className="text-sm">
                    Selecione uma empresa e um curso para listar os usuários.
                  </p>
                </div>
              ) : listLoading ? (
                <div className="flex h-[260px] items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : visibleUsers.length === 0 ? (
                <div className="flex h-[260px] flex-col items-center justify-center gap-2 px-6 text-center text-muted-foreground">
                  <UsersRound className="h-8 w-8 opacity-40" />
                  <p className="text-sm">
                    {search
                      ? 'Nenhum usuário corresponde à busca.'
                      : 'Nenhum usuário nesta empresa.'}
                  </p>
                </div>
              ) : (
                <div className="p-1.5">
                  {visibleUsers.map((user) => {
                    const isEnrolled = enrolledSet.has(user.id)
                    const isSelected = selectedUserIds.has(user.id)

                    return (
                      <div
                        key={user.id}
                        onClick={() => !isEnrolled && !isPending && toggleOne(user.id)}
                        className={[
                          'flex items-center gap-3 rounded-md px-2.5 py-2 transition-colors',
                          isEnrolled ? 'opacity-60' : 'cursor-pointer hover:bg-muted/60',
                          isSelected && !isEnrolled ? 'bg-primary/5' : '',
                        ].join(' ')}
                      >
                        <Checkbox
                          checked={isEnrolled ? false : isSelected}
                          disabled={isEnrolled || isPending}
                          onCheckedChange={() => toggleOne(user.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.avatar} />
                          <AvatarFallback className="text-xs">
                            {user.name.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{user.name}</p>
                          <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                        </div>
                        {isEnrolled && (
                          <Badge
                            variant="outline"
                            className="shrink-0 gap-1 border-primary/30 text-primary"
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            Já matriculado
                          </Badge>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </ScrollArea>

            {/* Rodapé da lista: contagem */}
            {bothChosen && !listLoading && (
              <div className="border-t border-border/60 px-3 py-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{selectedUserIds.size}</span> de{' '}
                {selectableUsers.length} selecionados
                {enrolledInCompany.length > 0 && ` · ${enrolledInCompany.length} já matriculado(s)`}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!bothChosen || selectedUserIds.size === 0 || isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Cadastrando...
              </>
            ) : (
              `Cadastrar (${selectedUserIds.size})`
            )}
          </Button>
        </DialogFooter>

        {/* Overlay de bloqueio durante a escrita no banco */}
        {isPending && (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-background/85 backdrop-blur-sm">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-sm font-medium">Cadastrando usuários...</p>
            <p className="text-xs text-muted-foreground">
              Não feche esta janela enquanto processamos.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
